/**
 * #1208 — Inbound-Management (Beta v1)
 *
 * READ-ONLY Cockpit für den Wareneingang (Lager): welche Anlieferungen kommen
 * wann, was ist drin, und welche Artikel sollten nach Umschlag (ABC nach Menge)
 * priorisiert eingelagert werden. Quelle: LIVE-OMS (read-only).
 *
 * @page /inbound-management
 * @roles operations (read)
 * @api /api/inbound/anlieferungen, /api/inbound/velocity, /api/inbound/bestand
 */
import { useState, useEffect, useCallback } from 'react';
import { PageHeader, Card, Badge, Button } from '../components/ui';
import BetaBadge from '../components/BetaBadge';
import { useLanguage } from '../context/LanguageContext';
import {
  Truck, Package, BarChart3, Boxes, Sparkles, ChevronDown, ChevronRight,
  AlertTriangle, Clock, RefreshCw, Info, CalendarClock, Users, X,
  ChevronLeft,
} from 'lucide-react';

function api(path) {
  return fetch(path, { credentials: 'include' }).then(async (r) => {
    if (!r.ok) {
      const txt = await r.text();
      let parsed; try { parsed = JSON.parse(txt); } catch { parsed = { error: txt }; }
      throw new Error(parsed.detail || parsed.error || `HTTP ${r.status}`);
    }
    return r.json();
  });
}

// ── ABC-Klasse → Badge-Variante ────────────────────────────────
function abcVariant(klasse) {
  switch (klasse) {
    case 'A': return 'segment-a';
    case 'B': return 'segment-b';
    case 'C': return 'segment-c';
    case 'neu': return 'success';
    case 'ruht': return 'default';
    default: return 'default';
  }
}
function komplexVariant(level) {
  return level === 'hoch' ? 'error' : level === 'mittel' ? 'warning' : 'success';
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(String(iso).replace(' ', 'T'));
  if (isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
function fmtNum(n) {
  return (Number(n) || 0).toLocaleString('de-AT', { maximumFractionDigits: 1 });
}

export default function InboundManagement() {
  const { t } = useLanguage();
  const [standort, setStandort] = useState('AT');
  const [tab, setTab] = useState('anlieferungen');
  const [abcFilter, setAbcFilter] = useState('all');
  const [data, setData] = useState({ anl: null, vel: null, best: null });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [anl, vel, best] = await Promise.all([
        api(`/api/inbound/anlieferungen?standort=${standort}`),
        api(`/api/inbound/velocity?standort=${standort}`),
        api(`/api/inbound/bestand?standort=${standort}`),
      ]);
      setData({ anl, vel, best });
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [standort]);

  useEffect(() => { load(); }, [load]);

  const stateLabel = (s) => t(`inbound.state.${s}`) || s;

  const TABS = [
    { key: 'anlieferungen', label: t('inbound.tab.deliveries'), Icon: Truck },
    { key: 'planer', label: t('inbound.tab.planer'), Icon: CalendarClock },
    { key: 'abc', label: t('inbound.tab.abc'), Icon: BarChart3 },
    { key: 'bestand', label: t('inbound.tab.stock'), Icon: Boxes },
  ];
  const STANDORTE = [
    { key: 'AT', label: 'AT' },
    { key: 'DE', label: 'DE' },
    { key: 'all', label: t('inbound.allLocations') },
  ];

  return (
    <div className="p-6">
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            {t('inbound.title')} <BetaBadge />
          </span>
        }
        subtitle={t('inbound.subtitle')}
        actions={
          <div className="flex items-center gap-3">
            {/* FlowAI-Stub — bewusst deaktiviert (#1208) */}
            <button
              type="button"
              disabled
              title={t('inbound.flowaiSoon')}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border border-line text-content-tertiary opacity-60 cursor-not-allowed"
            >
              <Sparkles style={{ width: 14, height: 14 }} />
              {t('inbound.flowaiButton')}
            </button>
            <Button variant="ghost" onClick={load} disabled={loading}>
              <RefreshCw style={{ width: 14, height: 14 }} className={loading ? 'animate-spin' : ''} />
              {t('common.refresh')}
            </Button>
          </div>
        }
      />

      {/* Standort-Switcher */}
      <div className="flex items-center gap-2 mb-4">
        <div className="inline-flex bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-line">
          {STANDORTE.map((s) => (
            <button
              key={s.key}
              onClick={() => setStandort(s.key)}
              className={`px-3 py-1.5 rounded-[8px] text-xs font-medium transition-all ${
                standort === s.key ? 'bg-surface-card shadow-sm text-content' : 'text-content-tertiary hover:text-content'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-5 border-b border-line">
        {TABS.map((tb) => (
          <button
            key={tb.key}
            onClick={() => setTab(tb.key)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === tb.key ? 'border-[#338edc] text-[#338edc]' : 'border-transparent text-content-tertiary hover:text-content'
            }`}
          >
            <tb.Icon style={{ width: 16, height: 16 }} />
            {tb.label}
          </button>
        ))}
      </div>

      {err && (
        <Card className="mb-4 border-red-500/30">
          <div className="flex items-center gap-2 text-sm text-red-600">
            <AlertTriangle style={{ width: 16, height: 16 }} />
            {t('inbound.loadError')}: {err}
          </div>
        </Card>
      )}

      {loading && !data.anl && (
        <div className="text-sm text-content-tertiary py-10 text-center">{t('common.loading')}…</div>
      )}

      {tab === 'anlieferungen' && data.anl && (
        <AnlieferungenView data={data.anl} t={t} stateLabel={stateLabel} />
      )}
      {tab === 'planer' && data.anl && (
        <PlanerView deliveries={data.anl.anlieferungen || []} standort={standort} t={t} />
      )}
      {tab === 'abc' && data.vel && (
        <AbcView data={data.vel} t={t} filter={abcFilter} setFilter={setAbcFilter} />
      )}
      {tab === 'bestand' && data.best && (
        <BestandView data={data.best} t={t} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ANLIEFERUNGEN
// ═══════════════════════════════════════════════════════════════
function AnlieferungenView({ data, t, stateLabel }) {
  const [open, setOpen] = useState({});
  if (!data.anlieferungen.length) {
    return <Card><div className="text-sm text-content-tertiary py-6 text-center">{t('inbound.noDeliveries')}</div></Card>;
  }
  return (
    <div className="space-y-3">
      <p className="text-xs text-content-tertiary flex items-center gap-1.5">
        <Info style={{ width: 13, height: 13 }} />
        {t('inbound.deliveriesHint', { count: data.count, buffer: data.buffer_days })}
      </p>
      {data.anlieferungen.map((a) => {
        const isOpen = !!open[a.id];
        return (
          <Card key={a.id} padding="sm" className={a.hat_a_artikel ? 'border-l-[3px] border-l-[#338edc]' : ''}>
            <button className="w-full flex items-center gap-3 text-left" onClick={() => setOpen((o) => ({ ...o, [a.id]: !o[a.id] }))}>
              <span className="text-content-tertiary">{isOpen ? <ChevronDown style={{ width: 16, height: 16 }} /> : <ChevronRight style={{ width: 16, height: 16 }} />}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm text-content">{a.name}</span>
                  {a.standort && <Badge variant="default">{a.standort}</Badge>}
                  <Badge variant="info">{stateLabel(a.state)}</Badge>
                  {a.hat_a_artikel && <Badge variant="segment-a" dot>{t('inbound.hasAArticle')}</Badge>}
                </div>
                {a.origin && <div className="text-xs text-content-tertiary mt-0.5 truncate">{t('inbound.origin')}: {a.origin}</div>}
              </div>
              <div className="hidden sm:flex flex-col items-end text-right shrink-0">
                <div className="text-xs text-content-tertiary flex items-center gap-1">
                  <Clock style={{ width: 12, height: 12 }} /> {fmtDate(a.scheduled_date)}
                </div>
                <div className="text-xs text-content-secondary mt-0.5">
                  {t('inbound.processableFrom')}: <span className="font-medium text-content">{fmtDate(a.bearbeitbar_ab)}</span>
                </div>
              </div>
              <div className="flex flex-col items-end shrink-0 ml-2">
                <Badge variant={komplexVariant(a.komplexitaet.level)}>{t(`inbound.complexity.${a.komplexitaet.level}`)}</Badge>
                <span className="text-[10px] text-content-muted mt-1">
                  {a.n_skus} {t('inbound.skus')} · {fmtNum(a.gesamtmenge)} {t('inbound.units')} · {a.packstuecke != null
                    ? `${fmtNum(a.packstuecke)} ${t('inbound.packstuecke')}`
                    : `${t('inbound.packstuecke')}: ${t('inbound.na')}`}
                </span>
              </div>
            </button>

            {isOpen && (
              <div className="mt-3 pt-3 border-t border-line">
                <p className="text-[11px] text-content-muted mb-2 flex items-center gap-1">
                  <Info style={{ width: 11, height: 11 }} /> {t('inbound.complexityEstimate')}
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[11px] text-content-tertiary uppercase tracking-wider border-b border-line">
                        <th className="text-left py-1.5 px-2">{t('inbound.product')}</th>
                        <th className="text-right py-1.5 px-2">{t('inbound.expectedQty')}</th>
                        <th className="text-center py-1.5 px-2">{t('inbound.abcClass')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {a.lines.map((l, i) => (
                        <tr key={i} className={`border-b border-line/50 ${l.is_a_artikel ? 'bg-[#338edc]/5' : ''}`}>
                          <td className="py-1.5 px-2 text-content">
                            {l.is_a_artikel && <span className="text-[#338edc] mr-1">●</span>}
                            {l.produkt}
                          </td>
                          <td className="py-1.5 px-2 text-right text-content tabular-nums">{fmtNum(l.erwartete_menge)}</td>
                          <td className="py-1.5 px-2 text-center">
                            {l.abc_klasse
                              ? <Badge variant={abcVariant(l.abc_klasse)}>{t(`inbound.class.${l.abc_klasse}`)}</Badge>
                              : <span className="text-content-muted text-xs">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ABC-ANALYSE
// ═══════════════════════════════════════════════════════════════
const OHNE_ZUORDNUNG_KEY = '__ohne__';
function skuKundeKey(s) {
  return s.company_id != null ? String(s.company_id) : OHNE_ZUORDNUNG_KEY;
}

function AbcView({ data, t, filter, setFilter }) {
  const kunden = data.kunden || [];
  // Sinnvoller Default: größter Kunde (kunden ist serverseitig nach #SKUs sortiert,
  // "ohne Zuordnung" ans Ende). Fällt auf "all" zurück, wenn keine Kunden.
  const [kunde, setKunde] = useState(() => (kunden[0] ? kunden[0].key : 'all'));
  useEffect(() => {
    setKunde(kunden[0] ? kunden[0].key : 'all');
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedKunde = kunden.find((k) => k.key === kunde) || null;
  const isAll = kunde === 'all';
  const counts = isAll ? (data.counts || {}) : (selectedKunde ? selectedKunde.counts : {});

  const FILTERS = [
    { key: 'all', label: t('inbound.filterAll') },
    { key: 'A', label: 'A' },
    { key: 'B', label: 'B' },
    { key: 'C', label: 'C' },
    { key: 'neu', label: t('inbound.class.neu') },
    { key: 'ruht', label: t('inbound.class.ruht') },
  ];

  const kundeLabel = (k) => k.ist_ohne_zuordnung
    ? t('inbound.ohneZuordnung')
    : (k.company_name || `#${k.company_id}`);

  const skus = (data.skus || [])
    .filter((s) => isAll || skuKundeKey(s) === kunde)
    .filter((s) => filter === 'all' || s.klasse === filter);

  return (
    <div className="space-y-4">
      {/* Kunden-Auswahl — ABC ist pro Kunde getrennt gerechnet */}
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs font-medium text-content-secondary">{t('inbound.customer')}</label>
        <select
          value={kunde}
          onChange={(e) => setKunde(e.target.value)}
          className="text-sm border border-line rounded-lg px-3 py-1.5 bg-surface-card text-content min-w-[220px]"
        >
          <option value="all">{t('inbound.allCustomers')}</option>
          {kunden.map((k) => (
            <option key={k.key} value={k.key}>
              {kundeLabel(k)} ({k.n_skus})
            </option>
          ))}
        </select>
      </div>

      <p className="text-xs text-content-tertiary flex items-center gap-1.5">
        <Info style={{ width: 13, height: 13 }} />
        {isAll ? t('inbound.abcCustomerAllHint') : t('inbound.abcCustomerHint')}
      </p>

      {/* Counts */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {['A', 'B', 'C', 'neu', 'ruht'].map((k) => (
          <Card key={k} padding="sm">
            <div className="flex items-center justify-between">
              <span className="text-xs text-content-tertiary">{t(`inbound.class.${k}`)}</span>
              <Badge variant={abcVariant(k)}>{counts[k] || 0}</Badge>
            </div>
            <div className="text-lg font-bold text-content mt-1">{counts[k] || 0}</div>
          </Card>
        ))}
      </div>

      <p className="text-xs text-content-tertiary flex items-center gap-1.5">
        <Info style={{ width: 13, height: 13 }} />
        {t('inbound.abcHint', { weeks: data.zeitraum_wochen, a: Math.round(data.thresholds.a * 100), b: Math.round(data.thresholds.b * 100) })}
      </p>

      {/* Filter */}
      <div className="inline-flex flex-wrap gap-1 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-line">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-[8px] text-xs font-medium transition-all ${
              filter === f.key ? 'bg-surface-card shadow-sm text-content' : 'text-content-tertiary hover:text-content'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* SKU-Tabelle */}
      <Card padding="sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] text-content-tertiary uppercase tracking-wider border-b border-line">
                <th className="text-left py-2 px-2">{t('inbound.product')}</th>
                {isAll && <th className="text-left py-2 px-2">{t('inbound.customer')}</th>}
                <th className="text-right py-2 px-2">{t('inbound.totalQty')}</th>
                <th className="text-right py-2 px-2">{t('inbound.avgPerWeek')}</th>
                <th className="text-right py-2 px-2">{t('inbound.cumShare')}</th>
                <th className="text-center py-2 px-2">{t('inbound.abcClass')}</th>
              </tr>
            </thead>
            <tbody>
              {skus.length === 0 && (
                <tr><td colSpan={isAll ? 6 : 5} className="py-6 text-center text-content-tertiary">{t('inbound.noSkus')}</td></tr>
              )}
              {skus.map((s) => (
                <tr key={s.product_id} className="border-b border-line/50 hover:bg-surface-elevated/40">
                  <td className="py-1.5 px-2 text-content">
                    {s.klasse === 'A' && <span className="text-[#338edc] mr-1">●</span>}
                    {s.produkt}
                  </td>
                  {isAll && (
                    <td className="py-1.5 px-2 text-content-tertiary text-xs truncate max-w-[200px]">
                      {s.company_name || t('inbound.ohneZuordnung')}
                    </td>
                  )}
                  <td className="py-1.5 px-2 text-right text-content tabular-nums">{fmtNum(s.total_qty)}</td>
                  <td className="py-1.5 px-2 text-right text-content tabular-nums">{fmtNum(s.avg_pro_woche)}</td>
                  <td className="py-1.5 px-2 text-right text-content-tertiary tabular-nums">{s.kumul_anteil != null ? `${s.kumul_anteil}%` : '—'}</td>
                  <td className="py-1.5 px-2 text-center">
                    <Badge variant={abcVariant(s.klasse)}>{t(`inbound.class.${s.klasse}`)}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// BESTAND
// ═══════════════════════════════════════════════════════════════
function BestandView({ data, t }) {
  return (
    <Card padding="sm">
      <p className="text-xs text-content-tertiary mb-3 flex items-center gap-1.5">
        <Info style={{ width: 13, height: 13 }} />
        {t('inbound.stockHint', { count: data.count })}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] text-content-tertiary uppercase tracking-wider border-b border-line">
              <th className="text-left py-2 px-2">{t('inbound.product')}</th>
              <th className="text-right py-2 px-2">{t('inbound.stockTotal')}</th>
              <th className="text-right py-2 px-2">{t('inbound.stockAvailable')}</th>
              <th className="text-left py-2 px-2">{t('inbound.locations')}</th>
            </tr>
          </thead>
          <tbody>
            {data.bestand.length === 0 && (
              <tr><td colSpan={4} className="py-6 text-center text-content-tertiary">{t('inbound.noStock')}</td></tr>
            )}
            {data.bestand.map((b) => (
              <tr key={b.product_id} className="border-b border-line/50 hover:bg-surface-elevated/40">
                <td className="py-1.5 px-2 text-content">{b.produkt}</td>
                <td className="py-1.5 px-2 text-right text-content tabular-nums">{fmtNum(b.gesamt)}</td>
                <td className="py-1.5 px-2 text-right text-content-secondary tabular-nums">{fmtNum(b.verfuegbar)}</td>
                <td className="py-1.5 px-2 text-content-tertiary text-xs truncate max-w-[280px]">
                  {b.locations.map((l) => l.location).join(', ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════
// ZEIT- & MA-PLANER (#1211)
// ═══════════════════════════════════════════════════════════════
function isoDay(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
/** Montag der Woche, in der `base` (+ weekOffset Wochen) liegt. */
function mondayOf(base, weekOffset) {
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  const js = d.getDay(); // 0=So..6=Sa
  const diffToMon = js === 0 ? -6 : 1 - js;
  d.setDate(d.getDate() + diffToMon + weekOffset * 7);
  return d;
}
function dayDatePart(v) {
  if (!v) return null;
  const s = String(v).replace(' ', 'T');
  return s.slice(0, 10);
}
function weekdayShort(iso) {
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('de-AT', { weekday: 'short' });
}
/** Komplexitäts-Last (PROXY) eines Tages → Level. KEINE Stunden-Zusage. */
function dayLoadLevel(load, headcount) {
  if (load <= 0) return null;
  if (headcount <= 0) return 'hoch';
  const ratio = load / headcount;
  if (ratio < 2) return 'niedrig';
  if (ratio < 5) return 'mittel';
  return 'hoch';
}

function PlanerView({ deliveries, standort, t }) {
  const [weekOffset, setWeekOffset] = useState(0);
  const [verf, setVerf] = useState(null);
  const [zuw, setZuw] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [selectedDay, setSelectedDay] = useState(null);
  const [saving, setSaving] = useState(false);

  const monday = mondayOf(new Date(), weekOffset);
  const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
  const von = isoDay(monday);
  const bis = isoDay(sunday);

  const loadPlaner = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [v, z] = await Promise.all([
        api(`/api/inbound/verfuegbarkeit?standort=${standort}&von=${von}&bis=${bis}`),
        api(`/api/inbound/zuweisungen?standort=${standort}&von=${von}&bis=${bis}`),
      ]);
      setVerf(v);
      setZuw(z.zuweisungen || []);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [standort, von, bis]);

  useEffect(() => { loadPlaner(); }, [loadPlaner]);

  // Tage der Woche (Mo..So)
  const weekDays = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday); d.setDate(monday.getDate() + i);
    weekDays.push(isoDay(d));
  }
  const verfByDay = {};
  (verf?.tage || []).forEach((tg) => { verfByDay[tg.datum] = tg; });

  // Anlieferungen je Tag bucketen — nach bearbeitbar_ab (Planungs-relevant)
  const deliveriesByDay = {};
  weekDays.forEach((d) => { deliveriesByDay[d] = []; });
  deliveries.forEach((a) => {
    const day = dayDatePart(a.bearbeitbar_ab) || dayDatePart(a.scheduled_date);
    if (day && deliveriesByDay[day]) deliveriesByDay[day].push(a);
  });

  // Zuweisungen je picking (über alle Tage der Woche)
  const zuwByPicking = {};
  zuw.forEach((z) => {
    (zuwByPicking[z.oms_picking_id] = zuwByPicking[z.oms_picking_id] || []).push(z);
  });

  const assign = async (picking, maId, day) => {
    setSaving(true); setErr('');
    try {
      await fetch('/api/inbound/zuweisungen', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          oms_picking_id: picking.id,
          oms_picking_name: picking.name,
          standort: picking.standort || (standort !== 'all' ? standort : null),
          mitarbeiter_id: maId,
          geplanter_tag: day,
        }),
      }).then(async (r) => { if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`); });
      await loadPlaner();
    } catch (e) {
      setErr(t('inbound.planer.assignError') + ': ' + (e.message || e));
    } finally { setSaving(false); }
  };
  const unassign = async (id) => {
    setSaving(true); setErr('');
    try {
      await fetch(`/api/inbound/zuweisungen/${id}`, { method: 'DELETE', credentials: 'include' })
        .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); });
      await loadPlaner();
    } catch (e) {
      setErr(t('inbound.planer.assignError') + ': ' + (e.message || e));
    } finally { setSaving(false); }
  };

  const noLagerMa = verf && verf.lager_ma_total === 0;
  const selVerf = selectedDay ? verfByDay[selectedDay] : null;
  const selDeliveries = selectedDay ? (deliveriesByDay[selectedDay] || []) : [];

  return (
    <div className="space-y-4">
      <p className="text-xs text-content-tertiary flex items-center gap-1.5">
        <Info style={{ width: 13, height: 13 }} /> {t('inbound.planer.intro')}
      </p>

      {/* Woche-Navigation */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => setWeekOffset((w) => w - 1)}>
            <ChevronLeft style={{ width: 14, height: 14 }} /> {t('inbound.planer.prevWeek')}
          </Button>
          <Button variant="ghost" onClick={() => setWeekOffset(0)}>{t('inbound.planer.thisWeek')}</Button>
          <Button variant="ghost" onClick={() => setWeekOffset((w) => w + 1)}>
            {t('inbound.planer.nextWeek')} <ChevronRight style={{ width: 14, height: 14 }} />
          </Button>
        </div>
        <span className="text-sm font-medium text-content">
          {t('inbound.planer.weekOf', { date: fmtDate(von) })}
          {loading && <RefreshCw style={{ width: 13, height: 13 }} className="inline ml-2 animate-spin" />}
        </span>
      </div>

      {err && (
        <Card className="border-red-500/30">
          <div className="flex items-center gap-2 text-sm text-red-600">
            <AlertTriangle style={{ width: 16, height: 16 }} /> {err}
          </div>
        </Card>
      )}
      {noLagerMa && (
        <Card className="border-amber-500/30">
          <div className="flex items-center gap-2 text-sm text-amber-600">
            <Info style={{ width: 16, height: 16 }} /> {t('inbound.planer.noLagerMa')}
          </div>
        </Card>
      )}

      {/* Kapazitäts-Hinweis */}
      <p className="text-[11px] text-content-muted flex items-start gap-1.5">
        <Info style={{ width: 12, height: 12, marginTop: 1 }} /> {t('inbound.planer.capacityHint')}
      </p>

      {/* Wochen-Timeline */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-2">
        {weekDays.map((day) => {
          const vd = verfByDay[day];
          const dels = deliveriesByDay[day] || [];
          const load = dels.reduce((s, a) => s + (a.komplexitaet?.raw || 0), 0);
          const headcount = vd?.headcount || 0;
          const level = dayLoadLevel(load, headcount);
          const isSel = selectedDay === day;
          const isToday = day === isoDay(new Date());
          return (
            <button
              key={day}
              onClick={() => setSelectedDay(isSel ? null : day)}
              className={`text-left rounded-lg border p-2.5 transition-all ${
                isSel ? 'border-[#338edc] ring-1 ring-[#338edc]/30' : 'border-line hover:border-[#338edc]/40'
              } ${vd?.ist_feiertag ? 'bg-[var(--bg-inset,#F8F9FC)]' : 'bg-surface-card'}`}
            >
              <div className="flex items-center justify-between">
                <span className={`text-xs font-semibold ${isToday ? 'text-[#338edc]' : 'text-content'}`}>
                  {weekdayShort(day)} {fmtDate(day).slice(0, 5)}
                </span>
                {vd?.ist_feiertag && <Badge variant="default">{t('inbound.planer.feiertag')}</Badge>}
              </div>
              <div className="mt-1.5 flex items-center gap-1 text-[11px] text-content-tertiary">
                <Users style={{ width: 12, height: 12 }} />
                <span className="text-content font-medium">{headcount}</span> {t('inbound.planer.headcount')}
              </div>
              {vd && vd.planned_hours_total > 0 && (
                <div className="text-[10px] text-content-muted mt-0.5">
                  {fmtNum(vd.planned_hours_total)} {t('inbound.planer.plannedHours')}
                </div>
              )}
              <div className="mt-2 flex items-center justify-between">
                <span className="text-[10px] text-content-muted">{dels.length} {t('inbound.tab.deliveries')}</span>
                {level && <Badge variant={komplexVariant(level)}>{t(`inbound.complexity.${level}`)}</Badge>}
              </div>
            </button>
          );
        })}
      </div>

      {/* Tagesplanung */}
      <Card padding="sm">
        <div className="flex items-center gap-2 mb-3">
          <CalendarClock style={{ width: 16, height: 16 }} className="text-[#338edc]" />
          <h3 className="text-sm font-semibold text-content">{t('inbound.planer.dayPlanning')}</h3>
          {selectedDay && <span className="text-xs text-content-tertiary">— {weekdayShort(selectedDay)} {fmtDate(selectedDay)}</span>}
        </div>

        {!selectedDay && (
          <p className="text-sm text-content-tertiary py-6 text-center">{t('inbound.planer.selectDay')}</p>
        )}

        {selectedDay && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Verfügbare MA */}
            <div className="lg:col-span-1">
              <p className="text-[11px] uppercase tracking-wider text-content-tertiary mb-2">{t('inbound.planer.availableMa')}</p>
              {selVerf?.ist_feiertag && (
                <Badge variant="default" className="mb-2">{t('inbound.planer.feiertag')}</Badge>
              )}
              {(!selVerf || selVerf.available.length === 0) ? (
                <p className="text-xs text-content-tertiary py-2">{t('inbound.planer.noAvailable')}</p>
              ) : (
                <ul className="space-y-1">
                  {selVerf.available.map((m) => (
                    <li key={m.mitarbeiter_id} className="flex items-center justify-between text-sm py-1 px-2 rounded bg-[var(--bg-inset,#F8F9FC)]">
                      <span className="text-content truncate">{m.name}</span>
                      <span className="flex items-center gap-1 shrink-0">
                        {m.teil_abwesend && <Badge variant="warning">{t('inbound.planer.teilAbwesend')}</Badge>}
                        <span className="text-[10px] text-content-muted">{fmtNum(m.planned_hours)}h</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Fällige Anlieferungen + Zuweisung */}
            <div className="lg:col-span-2">
              <p className="text-[11px] uppercase tracking-wider text-content-tertiary mb-2">{t('inbound.planer.deliveriesDue')}</p>
              {selDeliveries.length === 0 ? (
                <p className="text-xs text-content-tertiary py-2">{t('inbound.planer.noDeliveriesDay')}</p>
              ) : (
                <div className="space-y-2">
                  {selDeliveries.map((a) => {
                    const aZuw = (zuwByPicking[a.id] || []).filter((z) => z.geplanter_tag === selectedDay);
                    const assignedIds = new Set(aZuw.map((z) => z.mitarbeiter_id));
                    const candidates = (selVerf?.available || []).filter((m) => !assignedIds.has(m.mitarbeiter_id));
                    return (
                      <div key={a.id} className={`rounded-lg border p-2.5 ${a.hat_a_artikel ? 'border-l-[3px] border-l-[#338edc]' : 'border-line'}`}>
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-sm font-medium text-content truncate">{a.name}</span>
                            <Badge variant={komplexVariant(a.komplexitaet.level)}>{t(`inbound.complexity.${a.komplexitaet.level}`)}</Badge>
                            {a.hat_a_artikel && <Badge variant="segment-a" dot>{t('inbound.hasAArticle')}</Badge>}
                          </div>
                          <span className="text-[10px] text-content-muted shrink-0">{a.n_skus} {t('inbound.skus')} · {fmtNum(a.gesamtmenge)} {t('inbound.units')}</span>
                        </div>

                        {/* Zugewiesene MA */}
                        <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                          {aZuw.length === 0 && <span className="text-[11px] text-content-muted">{t('inbound.planer.unassigned')}</span>}
                          {aZuw.map((z) => (
                            <span key={z.id} className="inline-flex items-center gap-1 text-[11px] bg-[#338edc]/10 text-[#338edc] rounded-full pl-2 pr-1 py-0.5">
                              {z.mitarbeiter_name}
                              <button onClick={() => unassign(z.id)} disabled={saving} title={t('inbound.planer.remove')} className="hover:bg-[#338edc]/20 rounded-full p-0.5">
                                <X style={{ width: 11, height: 11 }} />
                              </button>
                            </span>
                          ))}
                          {/* Zuweisen */}
                          {candidates.length > 0 && (
                            <select
                              value=""
                              disabled={saving}
                              onChange={(e) => { if (e.target.value) assign(a, parseInt(e.target.value, 10), selectedDay); }}
                              className="text-[11px] border border-line rounded-full px-2 py-0.5 bg-surface-card text-content-secondary"
                            >
                              <option value="">+ {t('inbound.planer.assignMa')}</option>
                              {candidates.map((m) => (
                                <option key={m.mitarbeiter_id} value={m.mitarbeiter_id}>{m.name}</option>
                              ))}
                            </select>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
